import { CircularProgress, Icon, makeStyles, Tooltip, Typography } from '@material-ui/core';
import MyCollapse from 'components/Collapse';
import { variables } from 'components/Theme';
import { enums } from 'helpers';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import MyTable from 'v2/components/Shared/MyTable';
import MyTableBody from 'v2/components/Shared/MyTable/MyTableBody';
import MyTableCol from 'v2/components/Shared/MyTable/MyTableCol';
import MyTableHeader from 'v2/components/Shared/MyTable/MyTableHeader';
import MyTableRow from 'v2/components/Shared/MyTable/MyTableRow';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { dateMomentFormat } from 'v2/formatters/date';
import useGetUser from 'v2/hooks/useGetUser';
import { useModal } from 'v2/hooks/useModal';
import { EPaymentStatus } from 'v2/interfaces/invoice';
import { EContractStatus } from 'v2/interfaces/subscriptions';

import type { ISubscriptionInvoice } from 'v2/interfaces/subscriptions';

import DueDateModal from './DueDateModal';

interface IProps {
  isLoading: boolean;
  invoices: ISubscriptionInvoice[];
  retryInvoices: () => void;
  contractId: number;
}

const headers = [
  { title: '', ordering: false, style: { width: 8, paddding: 0 } },
  { title: 'Fatura', ordering: false },
  { title: 'Vencimento', ordering: false },
  { title: 'Pagamento', ordering: false },
  { title: 'Valor', ordering: false },
];

const useStyles = makeStyles((theme) => ({
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  container: {
    padding: 8,
  },
  icon: {
    fontSize: 14,
    cursor: 'pointer',
    marginLeft: 2,
  },
  tableStatus: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '0.65rem',
    padding: '0 !important',
    zIndex: 10,
    borderRadius: 6,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    ['&.paga, &.negociada' as any]: {
      backgroundColor: variables.palette.success.main,
    },
    ['&.reembolso, &.reembolsado' as any]: {
      backgroundColor: variables.palette.error.main,
    },
    ['&.aberta' as any]: {
      backgroundColor: variables.palette.primary.light,
    },
    ['&.aberta-blue' as any]: {
      backgroundColor: '#2d86ca',
    },
    ['&.recuperacao' as any]: {
      backgroundColor: '#fabb0a',
    },
    [theme.breakpoints.down('md')]: {
      width: '0.75rem',
      height: '0.75rem',
      top: '50%',
      left: '50%',
      borderRadius: 30,
      transform: 'translate3d(-50%, -50%, 0)',
    },
  },
}));

const PaymentHistory = memo(({ isLoading, invoices, retryInvoices, contractId }: IProps) => {
  const classes = useStyles();
  const user = useGetUser();

  const [showDueDateModal, setShowDueDateModal] = useModal();
  const [dueDateEditing, setDueDateEditing] = useState(null);
  const [invoiceIdEditing, setInvoiceIdEditing] = useState(null);

  const openInvoiceDueDateModal = useCallback(
    (invoiceId: number, dueDate: string) => {
      setDueDateEditing(dueDate);
      setInvoiceIdEditing(invoiceId);
      setShowDueDateModal();
    },
    [setShowDueDateModal]
  );

  const renderView = useMemo(() => {
    if (isLoading) {
      return (
        <div className={classes.center}>
          <CircularProgress size={22} />
        </div>
      );
    }

    if (!invoices?.length) {
      return <Typography variant='body2'>Nenhuma fatura encontrada</Typography>;
    }

    return (
      <MyTable responsive={false}>
        <MyTableHeader index={headers} />
        <MyTableBody>
          {invoices.map((item) => {
            const userIsProducer = user?.id === item.producer_id;

            return (
              <MyTableRow key={`item-${item.id}`}>
                <MyTableCol status screenSizes={{ xs: 1, sm: 1, md: 6, lg: 3 }} mobileOrder={1}>
                  <Tooltip placement='top' title={item.payment_status.name}>
                    <div className={`${classes.tableStatus} ${enums.INVOICE_STATUS_CLASSNAME[item.payment_status ? item.payment_status.id : 0]}`} />
                  </Tooltip>
                </MyTableCol>
                <MyTableCol screenSizes={{ xs: 1, sm: 1, md: 3, lg: 3 }} mobileOrder={1}>
                  <div>
                    <div>{item.id}</div>
                  </div>
                </MyTableCol>
                <MyTableCol screenSizes={{ xs: 1, sm: 1, md: 3, lg: 3 }} mobileOrder={1}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>{dateMomentFormat(item?.expired_at)}</div>
                    {userIsProducer &&
                      item.payment_status.id === EPaymentStatus.ABERTA &&
                      (item.payment_method.id === EContractStatus.EM_DIA || item.payment_method.id === EContractStatus.AGUARDANDO_PAGAMENTO) && (
                        <Icon onClick={() => openInvoiceDueDateModal(item.id, item.expired_at)} className={classes.icon}>
                          edit
                        </Icon>
                      )}
                  </div>
                </MyTableCol>
                <MyTableCol screenSizes={{ xs: 1, sm: 1, md: 3, lg: 3 }} mobileOrder={1}>
                  <div>{dateMomentFormat(item?.received_at)}</div>
                </MyTableCol>
                <MyTableCol numeric screenSizes={{ xs: 1, sm: 1, md: 3, lg: 3 }} mobileOrder={1}>
                  <div>
                    <PermissionHide inverse action='myeduzz:meus-contratos:ocultar-valores' fallback={<em>oculto</em>}>
                      <FormattedNumber value={item.amount} format='BRL' />
                    </PermissionHide>
                  </div>
                </MyTableCol>
              </MyTableRow>
            );
          })}
        </MyTableBody>
      </MyTable>
    );
  }, [classes.center, classes.icon, classes.tableStatus, invoices, isLoading, openInvoiceDueDateModal, user]);

  return (
    <Fragment>
      <DueDateModal
        isOpen={showDueDateModal}
        invoiceId={invoiceIdEditing}
        dueDate={dueDateEditing}
        onClose={setShowDueDateModal}
        contractId={contractId}
        retryInvoices={retryInvoices}
      />

      <MyCollapse title='Histórico de Pagamento' arrow noPadding>
        <div className={classes.container}>{renderView}</div>
      </MyCollapse>
    </Fragment>
  );
});

export default PaymentHistory;
